<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, unref } from "vue";
import SideMenuItem from "@/v2/components/SideMenu/shared/SideMenuItem.vue";
import useScrollSkewAnimation from "@/v2/shared/useScrollSkewAnimation.js";
import useScrollPersistence from "@/v2/shared/useScrollPersistence.js";
import useScrollDrag from "@/v2/shared/useScrollDrag.js";

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  selected: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["selected"]);

const expandedItemIds = ref([]);

const scrollerRef = ref(null);

const { destroy, reload } = useScrollSkewAnimation(
  ".side-menu",
  ".side-menu-item--text"
);

function onExpandItem(item) {
  if (expandedItemIds.value.includes(item.id)) {
    expandedItemIds.value = expandedItemIds.value.filter((i) => i !== item.id);
  } else {
    expandedItemIds.value.push(item.id);
  }
}

function onSelectItem(item) {
  emit("selected", item);
}

/*
This will scroll the selected item to the middle of the side menu.
 */
function scrollToSelected() {
  const selectedEl = scrollerRef.value.querySelector(
    `#side-menu-id-${props.selected}`
  );
  if (selectedEl) {
    const top =
      selectedEl.offsetTop -
      scrollerRef.value.clientHeight / 2 +
      selectedEl.clientHeight / 2;
    scrollerRef.value.scrollTo({ top });
  }
}

/*
This function will return the ID of the expanded item as well as the IDs
of each ancestor.
 */
function getExpandedIds(items, selectedId) {
  function search(array, path) {
    for (let i = 0; i < array.length; i += 1) {
      const obj = array[i];
      // Add the current object's ID to the path
      const currentPath = [...path, obj.id];
      // Check if the current object's id matches the target id
      if (obj.id === selectedId) {
        // Return the path if the ID is found
        return currentPath;
      }
      // If the current object has children, recursively search within them
      if (obj.children && obj.children.length > 0) {
        const result = search(obj.children, currentPath);
        if (result) {
          return result;
        }
      }
    }
    return null; // Return null if the object with the given id is not found
  }

  // Start the search from the root array with an empty path
  return search(items, []);
}

onMounted(async () => {
  if (props.selected) {
    const expandedIds = getExpandedIds(unref(props.items), props.selected);
    expandedItemIds.value = expandedItemIds.value.concat(expandedIds);
  }

  await reload();

  if (props.selected) {
    // Requires waiting for two cycles before scroll is ready!
    await nextTick();
    await nextTick();
    scrollToSelected();
  }

  await nextTick();
  const firstMenuItem = scrollerRef.value?.querySelector(
    ".side-menu-item--text"
  );
  if (firstMenuItem) {
    firstMenuItem.focus();
  }
});

onBeforeUnmount(() => {
  destroy();
});

watch(
  () => props.items.length,
  () => {
    reload();
  }
);

const { handleScrollPersistence } = useScrollPersistence();

handleScrollPersistence(scrollerRef, "vertical", "sidebar");

useScrollDrag({
  container: scrollerRef,
});
</script>

<template>
  <div
    ref="scrollerRef"
    class="side-menu"
    focus-theme="dark"
    data-testid="side-menu"
  >
    <ul class="side-menu--inner">
      <SideMenuItem
        v-for="(item, i) in items"
        :key="`item-${i}`"
        :item="item"
        :expanded-item-ids="expandedItemIds"
        @on-expand-item="onExpandItem"
        @on-select-item="onSelectItem"
      />
    </ul>
  </div>
</template>

<style scoped lang="scss">
.side-menu {
  display: flex;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  position: absolute;
  padding: var(--spacing-md);
  left: 0;
  top: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  &--inner {
    display: flex;
    flex-direction: column;
    padding: 0;
    padding-block-end: 1200px;
    gap: var(--spacing-base-sm);
    margin: 0;
    list-style: none;
  }
}
</style>
